import React, { useEffect, useState, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import MultiSelectNotice from '../../components/Common/MultiSelectNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import html2canvas from 'html2canvas'
import { exportToPNG } from '../../utils/exportUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { expandMonthName } from '../../utils/dataUtils'
import './StructureMeasures.css'

const MEASURES = [
    {
        key: 'Coordination - Engaging Community Providers',
        name: 'Coordination',
        description: <>Coordinate quality improvement efforts to engage &amp; share resources with outpatient prenatal<br />clinics, federally qualified health centers and other community health clinics affiliated with your<br />hospital with ILPQC QI initiative(s).</>
    },
    {
        key: 'Community Partnerships',
        name: 'Partnerships',
        description: <>Build relationships with community-based doulas, home visiting programs or other community<br />resources in your hospital catchment to create points of access to improve<br />referral of prenatal and postpartum patients to these community resources.</>
    },
    {
        key: 'Doula-Friendly Culture',
        name: 'Doulas',
        description: <>Promote doula-friendly unit culture and strategies to support community-based doula<br />participation with your maternity care team.</>
    },
    {
        key: 'Data Review Sharing',
        name: 'Data Review',
        description: <>Implement a strategy to share quality improvement QI data progress with clinical staff (e.g. post<br />data, share QI progress in provider meetings/grand rounds, etc.).</>
    },
]

const STATUS_CONFIG: Record<string, { className: string, label: string }> = {
    green: { className: 'green', label: 'In place' },
    yellow: { className: 'yellow', label: 'Working on it' },
    red: { className: 'red', label: 'Haven\'t started' },
    '2': { className: 'green', label: 'In place' },
    '1': { className: 'yellow', label: 'Working on it' },
    '0': { className: 'red', label: 'Haven\'t started' },
}

const StructureMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection()
    const [statusMap, setStatusMap] = useState<Record<string, string | null>>({})
    const [loading, setLoading] = useState(true)
    const [reportingMonth, setReportingMonth] = useState<string>("")
    const [hasData, setHasData] = useState<boolean>(true)
    const exportRef = useRef<HTMLDivElement>(null)
    const printExportRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isMultiSelect || selectedHospitalIds.length === 0) {
            setStatusMap({})
            setHasData(false)
            setLoading(false)
            return
        }

        const hospitalId = selectedHospitalIds[0]
        setLoading(true)

        fetch(`/api/structure_measures/${hospitalId}`)
            .then(res => res.json())
            .then(result => {
                const rows = result?.response?.result?.rows || []
                const filteredRows = rows.filter((r: any) => r[0] === hospitalId)

                if (filteredRows.length > 0) {
                    setReportingMonth(expandMonthName(filteredRows[0][2]))
                    const newStatusMap: Record<string, string | null> = {}
                    filteredRows.forEach((row: any) => {
                        const measureName = row[3]
                        const qualityExcellence = row[4]
                        if (measureName) {
                            newStatusMap[measureName] = qualityExcellence || null
                        }
                    })
                    setStatusMap(newStatusMap)
                    setHasData(true)
                } else {
                    setStatusMap({})
                    setHasData(false)
                    setReportingMonth("")
                }
                setLoading(false)
            })
            .catch(err => {
                console.error("Error fetching structure measures:", err)
                setStatusMap({})
                setHasData(false)
                setLoading(false)
            })
    }, [selectedHospitalIds, isMultiSelect, refreshKey])

    const getStatusIndicator = (measureKey: string) => {
        const value = statusMap[measureKey]
        if (!value) {
            return <span className="status-pna">PNA</span>
        }
        const config = STATUS_CONFIG[value.toLowerCase()] || STATUS_CONFIG[value]
        if (!config) {
            return <span className="status-pna">PNA</span>
        }
        return (
            <div className="status-indicator-wrapper" title={config.label}>
                <span className={`status-dot ${config.className}`}></span>
            </div>
        )
    }

    const handleDownloadPNG = async () => {
        if (printExportRef.current) {
            await exportToPNG(printExportRef.current, 'structure_measures', undefined, {
                forceDesktopCapture: true
            })
        }
    }

    const handleDownloadSVG = async () => {
        if (printExportRef.current) {
            try {
                const canvas = await html2canvas(printExportRef.current, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#f0f6fa',
                } as any)
                const width = canvas.width / 2
                const height = canvas.height / 2
                const dataURL = canvas.toDataURL('image/png')
                const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="#f0f6fa"/>
  <image x="0" y="0" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`
                const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = 'Structure_Measures.svg'
                link.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.error('Error generating SVG:', error)
            }
        }
    }

    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation()
        const menu = document.getElementById('export-dropdown-menu-structure')
        if (menu) menu.classList.toggle('show')
    }

    const closeMenu = () => {
        const menu = document.getElementById('export-dropdown-menu-structure')
        if (menu) menu.classList.remove('show')
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.export-menu-container')) {
                closeMenu()
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    if (loading) {
        return <LoadingNotice title="Loading structure measures data..." />
    }

    const showNoDataOverlay = !hasData && !loading && !isMultiSelect
    const showAggregateNotice = isMultiSelect

    return (
        <div className="bcbs-structure-measures-page" data-export-target={generatePDFIds.exportTarget('structure-measures')} style={isMultiSelect ? { overflow: 'hidden', minHeight: 'calc(100vh - 80px)' } : showNoDataOverlay ? { position: 'relative', minHeight: 'calc(100vh - 80px)' } : undefined}>
            <MultiSelectNotice visible={showAggregateNotice} />
            <NoDataOverlay visible={showNoDataOverlay} title="No Data Available" icon="⚠️" />

            {!showAggregateNotice && !showNoDataOverlay && (
            <div ref={exportRef}>
                <div className="page-header">
                    <div className="header-left">
                        <h1 className="page-title">Structure Measures</h1>
                        <p className="reporting-month">Reporting Month: {reportingMonth || "NA"}</p>
                    </div>

                    <div className="header-right">
                        <div className="legend-bar">
                            <div className="legend-item">
                                <span className="legend-dot green"></span>
                                <span>In place</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot yellow"></span>
                                <span>Working on it</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-dot red"></span>
                                <span>Haven't started</span>
                            </div>
                        </div>
                    </div>
                </div>

                {(
                    <div className="report-container">
                        <div className="export-menu-container">
                            <button className="hamburger-btn" onClick={toggleMenu}>&#9776;</button>
                            <div id="export-dropdown-menu-structure" className="export-dropdown">
                                <div onClick={() => { closeMenu(); handleDownloadPNG() }}>Download PNG</div>
                                <div onClick={() => { closeMenu(); handleDownloadSVG() }}>Download SVG</div>
                            </div>
                        </div>
                        <table id={generatePDFIds.table('structure')} className="measures-table">
                            <thead>
                                <tr>
                                    <th className="col-name"></th>
                                    <th className="col-desc">Measures</th>
                                    <th className="col-status">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MEASURES.filter((measure) => measure.key in statusMap).map((measure) => (
                                    <tr key={measure.key}>
                                        <td className="measure-name">{measure.name}</td>
                                        <td className="measure-desc">{measure.description}</td>
                                        <td className="status-cell">{getStatusIndicator(measure.key)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}

            {!showNoDataOverlay && !isMultiSelect && (
                <div ref={printExportRef} className="bcbs-structure-measures-page" style={{ position: 'absolute', left: '-9999px', top: 0, width: '960px', background: '#f0f6fa', padding: '1rem' }}>
                    <div className="page-header">
                        <div className="header-left">
                            <h1 className="page-title">Structure Measures</h1>
                            <p className="reporting-month">Reporting Month: {reportingMonth || "NA"}</p>
                        </div>
                        <div className="header-right">
                            <div className="legend-bar">
                                <div className="legend-item">
                                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#43A047', display: 'inline-block', flexShrink: 0 }}></span>
                                    <span>In place</span>
                                </div>
                                <div className="legend-item">
                                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#FDD835', display: 'inline-block', flexShrink: 0 }}></span>
                                    <span>Working on it</span>
                                </div>
                                <div className="legend-item">
                                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#E53935', display: 'inline-block', flexShrink: 0 }}></span>
                                    <span>Haven't started</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="report-container">
                        <table className="measures-table">
                            <thead>
                                <tr>
                                    <th className="col-name"></th>
                                    <th className="col-desc">Measures</th>
                                    <th className="col-status">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MEASURES.filter((measure) => measure.key in statusMap).map((measure) => (
                                    <tr key={measure.key}>
                                        <td className="measure-name">{measure.name}</td>
                                        <td className="measure-desc">{measure.description}</td>
                                        <td className="status-cell">{getStatusIndicator(measure.key)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default StructureMeasures
